import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { leaveProject } from "../../api/members";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { useAppStore } from "../../store/useAppStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface LeaveProjectDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeaveProjectDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: LeaveProjectDialogProps) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { removeProject } = useAppStore();

  const handleLeave = async () => {
    setLoading(true);
    try {
      await leaveProject(projectId);
      // Remove from the local store so the project list updates immediately
      // without requiring a full refetch.
      removeProject(projectId);
      notify.success("Left project", { description: projectName });
      navigate("/projects");
    } catch (err: any) {
      // 409 means the user is the sole owner — they must transfer ownership first.
      if (err?.response?.status === 409) {
        notify.error(
          "You are the sole owner. Transfer ownership before leaving."
        );
        onOpenChange(false);
      } else {
        handleError(err, { scope: "LeaveProjectDialog", context: { projectId } });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave "{projectName}"?</AlertDialogTitle>
          <AlertDialogDescription>
            You will lose access to this project immediately. To regain access
            you will need to be invited again by an owner.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              handleLeave();
            }}
          >
            {loading ? "Leaving…" : "Leave project"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
